/**
 * PromoterInviteHeader.tsx - Promoter Invitation Page Header
 *
 * This component displays the header section of promoter invitation pages
 * with fundraiser branding, key information, and promotional messaging.
 *
 * Key Features:
 * - Fundraiser branding with logo/icon and colors
 * - Key fundraiser information and progress
 * - Promotional messaging for promoter recruitment
 * - Status badges and expiration warnings
 * - Responsive design with mobile optimization
 * - Share functionality for viral promotion
 *
 * Header Information:
 * - Fundraiser logo/icon with custom theming
 * - Title and subtitle with compelling messaging
 * - Current progress and goal information
 * - Time remaining and urgency indicators
 * - Organization and cause information
 *
 * Promotional Elements:
 * - Compelling headlines for promoter recruitment
 * - Benefits and incentives for joining
 * - Social proof and success indicators
 * - Urgency messaging for time-sensitive campaigns
 *
 * Visual Design:
 * - Custom color theming from fundraiser settings
 * - Responsive layout for all screen sizes
 * - Eye-catching design elements and typography
 * - Status badges and progress indicators
 *
 * Props: None (uses MobX store and URL parameters)
 *
 * Dependencies: MobX store, badge utilities, color theming
 */

import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { observer } from "mobx-react-lite";
import { Droplets, Share2 } from "lucide-react";
import * as Icons from "lucide-react";
import { fundraiserStore } from "../../stores/FundraiserStore";
import {
  shouldShowExpirationBadge,
  getExpirationBadgeText,
  shouldShowPendingBadge,
  getPendingBadgeText,
} from "../../utils/badgesUtils";
import {
  isLightColor,
  darkenColor,
  lightenColor,
} from "../../utils/colorUtils";
import { FundRaiserStatus, IconType } from "../../enums";
import { NavigateFunction } from "react-router-dom";
import { withRouter } from "@mindmemobile/mm-shared-global";
import { ColorModel } from "@mindmemobile/mm-payment-modules";
import { FundraiserCustomColorConfig } from "../../models/FundraiserModels";

const HeaderContainer = styled.header<{
  isScrolled: boolean;
  customColors?: any;
}>`
  border-bottom: ${(props) =>
    props.isScrolled ? "1px solid #e2e8f0" : "none"};
  padding: ${(props) => (props.isScrolled ? "16px 0" : "41px 0")};
  position: sticky;
  top: 0;
  z-index: 100;
  transition: all 0.3s ease;
  background: ${(props) => props.customColors?.background.rgba || "#f8fafc"};
  backdrop-filter: ${(props) => (props.isScrolled ? "blur(12px)" : "none")};
  box-shadow: ${(props) =>
    props.isScrolled ? "0 4px 20px rgba(0, 0, 0, 0.1)" : "none"};
`;

const HeaderContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 16px;
    align-items: stretch;
  }
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  @media (max-width: 768px) {
    flex: 1;
    justify-content: center;
  }
`;

const LogoIcon = styled.div<{ isLogo?: boolean; backgroundColor?: string }>`
  width: ${(props) => (props.isLogo ? "auto" : "48px")};
  max-width: ${(props) => (props.isLogo ? "150px" : "48px")};
  height: 48px;
  background: ${(props) =>
    props.backgroundColor || "linear-gradient(135deg, #0ea5e9, #0284c7)"};
  border-radius: ${(props) => (props.isLogo ? "8px" : "50%")};
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  overflow: hidden;
  transition: all 0.2s ease;
  padding: ${(props) => (props.isLogo ? "8px" : "0")};
  &.min-width-logo{
    min-width: 48px;
  }
`;

const LogoImage = styled.img`
  max-width: 150px;
  max-height: 48px;
  width: auto;
  height: auto;
  object-fit: contain;
  border-radius: 6px;
`;

const LogoText = styled.div`
  display: flex;
  flex-direction: column;
`;

const BrandName = styled.h1<{ textColor?: string }>`
  font-size: 28px;
  font-weight: 800;
  color: ${(props) => props.textColor || "#1e293b"};
  margin: 0;
  line-height: 1;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const BrandTagline = styled.p<{ textColor?: string }>`
  font-size: 14px;
  color: ${(props) => props.textColor || "#64748b"};
  margin: 0;
  margin-top: 10px;
  margin-top: 5px;
  font-weight: 500;
`;

const HeaderCenter = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  @media (max-width: 768px) {
    justify-content: center;
    gap: 12px;
    flex: 1;
    width: 100%;
    padding-top: 20px;
  }
`;

const PendingBadge = styled.div`
  background: rgba(59, 130, 246, 0.95);
  color: #ffffff;
  font-size: 10px;
  font-weight: 700;
  padding: 6px 8px;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  backdrop-filter: blur(8px);
  white-space: nowrap;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
`;

const ExpirationBadge = styled.div`
  background: rgba(220, 38, 38, 0.95);
  color: #ffffff;
  font-size: 10px;
  font-weight: 700;
  padding: 6px 8px;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  backdrop-filter: blur(8px);
  white-space: nowrap;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 8px rgba(220, 38, 38, 0.3);
`;

const ActionButton = styled.button`
  padding: 16px 24px;
  height: 44px;
  background: #ffffff;
  color: #64748b;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: center;
  white-space: nowrap;
  min-width: fit-content;

  &:hover {
    background: #f8fafc;
    border-color: #cbd5e1;
  }
`;

const JoinButton = styled.button`
  padding: 16px 24px;
  height: 44px;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: center;
  white-space: nowrap;
  min-width: fit-content;

  &:hover {
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

interface PromoterInviteHeaderProps {
  onShareClick?: () => void;
  customColors?: FundraiserCustomColorConfig;
  navigate: NavigateFunction;
  promoterId?: string;
}

const PromoterInviteHeader: React.FC<PromoterInviteHeaderProps> = observer(
  ({ onShareClick, customColors, navigate, promoterId }) => {
    const [isScrolled, setIsScrolled] = useState(false);

    // Get custom colors from campaign store if not provided as props
    const builderColors = fundraiserStore.builderData?.customColors
    const colors = customColors || builderColors;

    // Ensure text color exists with fallback
    const textColor = colors.text?.hex || "#1e293b";
    const lighterTextColor = lightenColor(textColor, 25);

    // Get selected fundraiser for badge logic
    const selectedFundraiser = fundraiserStore.selectedFundraiser;

    // Badge logic with priority (Pending takes priority over Expiration)
    const shouldShowPendingBadgeInHeader = shouldShowPendingBadge(
      selectedFundraiser?.startDate || null,
      selectedFundraiser?.status || ""
    );
    const shouldShowExpirationBadgeInHeader =
      !shouldShowPendingBadgeInHeader &&
      shouldShowExpirationBadge(
        selectedFundraiser?.endDate || null,
        selectedFundraiser?.status || FundRaiserStatus.Active
      );

    // Get builder data for helper functions
    const builderData = fundraiserStore.builderData;

    // Helper functions moved inside component scope
    const getFundraiserTitle = () => {
      return (
        builderData?.title || selectedFundraiser?.title || "" //|| "Clean Water for All"
      );
    };

    const getFundraiserTagline = () => {
      return builderData?.subtitle || selectedFundraiser?.description;
    };

    const getButtonText = () => {
      return builderData?.ctaButtonText || "Donate";
    };

    const getFundraiserIcon = () => {
      const iconImageChoice = builderData?.iconImageChoice || IconType.None;

      if (iconImageChoice === IconType.Icon) {
        const iconName = builderData?.titleIcon || "Droplets";
        return iconName;
      }

      // Default fallback icon
      return "Droplets";
    };

    const getFundraiserImage = () => {
      // Use the specific fundraiser logo image from builder data
      return builderData?.fundraiserImage?.fullURL || null;
    };

    const getIconColor = () => {
      return builderData?.titleColor || "#0ea5e9";
    };

    const shouldShowImage = () => {
      const iconImageChoice = builderData?.iconImageChoice || IconType.None;
      return iconImageChoice === IconType.Image && getFundraiserImage()
        ? true
        : false;
    };

    const getIconColors = () => {
      const selectedColor = getIconColor();
      const hex =
        typeof selectedColor === "string" ? selectedColor : selectedColor.hex;
      const bgColor = hex;
      const iconColor = isLightColor(hex) ? darkenColor(hex) : "#ffffff";
      return { bgColor, iconColor };
    };

    const renderIcon = (iconName: string) => {
      const Icon = (Icons as any)[iconName];
      return Icon ? <Icon size={28} /> : <Droplets size={28} />;
    };

    useEffect(() => {
      const handleScroll = () => {
        const scrollTop =
          window.pageYOffset || document.documentElement.scrollTop;
        setIsScrolled(scrollTop > 50);
      };

      window.addEventListener("scroll", handleScroll, { passive: true });

      return () => {
        window.removeEventListener("scroll", handleScroll);
      };
    }, []);

    const handleDonateClick = () => {
      // Navigate to fundraiser page for donation
      const fundraiserId =
        fundraiserStore.selectedFundraiserId || selectedFundraiser?.id;
      if (fundraiserId) {
        fundraiserStore.setSelectedFundraiser(fundraiserId);
        navigate(`/fundraiser/${fundraiserId}?PID=${promoterId || ""}`);
      }
    };

    const handleJoinClick = () => {
      // Scroll to join form on promoter invite page
      const joinForm = document.querySelector("[data-join-form]");
      if (joinForm) {
        const targetPosition =
          joinForm.getBoundingClientRect().top + window.scrollY - 125;
        window.scrollTo({ top: targetPosition, behavior: "smooth" });
      }
    };

    const handleShareClick = () => {
      if (onShareClick) {
        onShareClick();
      }
    };

    // Get Join Us button styles based on donation button design
    // Get button styles from store
    const getJoinButtonStyles = () => {
      const baseStyles = fundraiserStore.getDonationButtonStyles();
      return {
        ...baseStyles,
        fontSize: "16px",
      } as React.CSSProperties & { "--hover-shadow": string };
    };

    return (
      <HeaderContainer isScrolled={isScrolled} customColors={colors}>
        <HeaderContent>
          <Logo>
            <LogoIcon
              backgroundColor={
                shouldShowImage() ? "transparent" : getIconColors().bgColor
              }
              isLogo={shouldShowImage()}
              className="min-width-logo"
            >
              {shouldShowImage() ? (
                <LogoImage
                  src={getFundraiserImage() || ""}
                  alt={getFundraiserTitle()}
                />
              ) : (
                <div style={{ color: getIconColors().iconColor }}>
                  {renderIcon(getFundraiserIcon())}
                </div>
              )}
            </LogoIcon>
            <LogoText>
              <BrandName textColor={textColor}>
                {getFundraiserTitle()}
                {shouldShowPendingBadgeInHeader &&
                  selectedFundraiser?.startDate && (
                    <PendingBadge>
                      {getPendingBadgeText(
                        new Date(selectedFundraiser.startDate)
                      )}
                    </PendingBadge>
                  )}
                {shouldShowExpirationBadgeInHeader &&
                  selectedFundraiser?.endDate && (
                    <ExpirationBadge>
                      {getExpirationBadgeText(
                        new Date(selectedFundraiser.endDate)
                      )}
                    </ExpirationBadge>
                  )}
              </BrandName>
              <BrandTagline textColor={lighterTextColor}>
                {getFundraiserTagline()}
              </BrandTagline>
            </LogoText>
          </Logo>

          <HeaderCenter>
            <ActionButton onClick={handleShareClick}>
              <Share2 size={16} />
              Share
            </ActionButton>

            <ActionButton onClick={handleDonateClick}>
              <Icons.Heart size={16} />
              {getButtonText()}
            </ActionButton>

            <JoinButton
              onClick={handleJoinClick}
              style={{
                ...getJoinButtonStyles(),
                fontSize: "16px",
                minWidth: "150px",
                whiteSpace: "nowrap",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = (
                  getJoinButtonStyles() as any
                )["--hover-shadow"];
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = getJoinButtonStyles()
                  .boxShadow as string;
              }}
            >
              Join Us
            </JoinButton>
          </HeaderCenter>
        </HeaderContent>
      </HeaderContainer>
    );
  }
);
export default withRouter(PromoterInviteHeader);
